import {
  AutoResponseMessage,
  HighPrioritySystemMessage,
  LowPrioritySystemMessage,
  MESSAGE_TYPES,
} from '@zg-rentals/satellite-components';
import { List } from '@zillow/constellation-10';
import { memo, useCallback } from 'react';
import { inboxPageShouldScrollToRenterProfileVariable } from '../../lib/apollo';
import type { MessageList_GetConversationQuery } from '../../types/GraphqlTypes';
import { SatelliteAiAutoResponseType } from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import useDevices from '../../hooks/useDevices';
import type { HandleOpenDeleteAttachmentsModal, HandleOpenImagesViewer } from '../MessageList';
import ApplicationRequest from './components/ApplicationRequest';
import ListingMessage from './components/ListingMessage';
import PhoneLead from './components/PhoneLead';

const { LISTING_MESSAGE, PHONE_LEAD, RENTER_PROFILE_SUBMISSION, SYSTEM_MESSAGE } = MESSAGE_TYPES;

const { APPLICATION_REQUEST } = SYSTEM_MESSAGE.APPLICATIONS;

export type MessageListMessage = ArrayElement<
  MessageList_GetConversationQuery['ZRM_getConversationById']['conversation']
>;
type Props = {
  isLastMessageInList: boolean;
  message: MessageListMessage;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
};

const MessageItem = ({
  isLastMessageInList,
  message: messageObject,
  openDeleteAttachmentsModal,
  openImagesViewer,
}: Props): JSX.Element => {
  const { isMobileView } = useDevices();
  const { changeMobileView } = useInboxRouterHelper();

  const {
    aiAutoResponseType,
    attachments,
    isMessageOwner,
    message: messageContent,
    messageDateMs,
    messageLink,
    messageType,
    senderName,
    title,
    uiTreatmentType,
  } = messageObject;

  const ctaText = messageLink?.text ?? '';
  const systemMessageUrl = messageLink?.url ?? '';
  const isHighPrioritySystemMessage =
    uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY || uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY_WITH_LOGO;
  const shouldDisplaySentStatus = !!messageDateMs && isLastMessageInList && !!isMessageOwner;

  const handleSystemMessageCtaClick = () => {
    if (messageType === RENTER_PROFILE_SUBMISSION) {
      if (isMobileView) {
        changeMobileView(InboxPageMobileView.RenterProfile);
      }

      // Handle scrolling to Renter Profile section in the 3rd column for RENTER_PROFILE_SUBMISSION system messages
      return inboxPageShouldScrollToRenterProfileVariable(true);
    }

    return window.open(systemMessageUrl, '_newtab');
  };

  const MessageItemContent = useCallback((): JSX.Element => {
    if (aiAutoResponseType === SatelliteAiAutoResponseType.EliseAi && messageContent) {
      return <AutoResponseMessage message={messageContent} messageDateMs={messageDateMs} metaLabelType="eliseAI" />;
    }

    switch (messageType) {
      case LISTING_MESSAGE:
        return (
          <ListingMessage
            attachments={attachments}
            isMessageOwner={isMessageOwner ?? false}
            messageContent={messageContent ?? ''}
            messageDateMs={messageDateMs}
            senderName={senderName ?? ''}
            shouldDisplaySentStatus={shouldDisplaySentStatus}
            messageId={messageObject.messageId ?? ''}
            uiTreatmentType={uiTreatmentType ?? ''}
            openDeleteAttachmentsModal={openDeleteAttachmentsModal}
            openImagesViewer={openImagesViewer}
          />
        );
      case PHONE_LEAD:
        return (
          <PhoneLead
            isMessageOwner={isMessageOwner ?? false}
            messageContent={messageContent ?? ''}
            messageDateMs={messageDateMs}
          />
        );
      case APPLICATION_REQUEST:
        return <ApplicationRequest messageObject={messageObject} />;
      default:
        return (
          <>
            {isHighPrioritySystemMessage ? (
              <HighPrioritySystemMessage
                ctaText={ctaText}
                message={messageContent ?? ''}
                messageDateMs={messageDateMs}
                messageType={messageType}
                onCtaClick={handleSystemMessageCtaClick}
                title={title ?? ''}
                uiTreatmentType={uiTreatmentType ?? ''}
              />
            ) : (
              <LowPrioritySystemMessage
                ctaText={ctaText}
                message={messageContent ?? ''}
                messageDateMs={messageDateMs}
                url={systemMessageUrl}
              />
            )}
          </>
        );
    }
  }, [
    aiAutoResponseType,
    messageContent,
    messageDateMs,
    senderName,
    shouldDisplaySentStatus,
    isMessageOwner,
    attachments,
    messageType,
    uiTreatmentType,
  ]);

  return (
    <List.Item
      aria-live={isLastMessageInList ? 'polite' : 'off'}
      aria-roledescription="message"
      data-testid="message-item"
    >
      <MessageItemContent />
    </List.Item>
  );
};

export default memo(MessageItem);
